import { Button } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useFormat } from '@brightdrop/localization-core';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import CloseIcon from '~/assets/icons/new/close.svg?react';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';

import ActivationCompletionRow from './ActivationCompletionRow';
import useStyles from './ActivationProgressSnackbar.styles';

const TRANSLATION_KEYS = {
  titleComplete: 'catalog:order.activationProgress.titleComplete',
  titleCompleteFallback:
    'catalog:order.activationProgress.titleCompleteFallback',
  cardActivated: 'catalog:order.activationProgress.cardActivated',
  cardFailed: 'catalog:order.activationProgress.cardFailed',
  reenrolled: 'catalog:order.activationProgress.reenrolled',
  reenrollFailed: 'catalog:order.activationProgress.reenrollFailed',
  enrolledVehicleCount: 'catalog:enrolledVehicleCount',
  tryAgain: 'catalog:order.activationProgress.tryAgain',
  close: 'catalog:order.activationProgress.close',
} as const;

export type ActivationCompletionCardProps = {
  /**
   * Total devices activated across the finished bulk batches. Its row is hidden
   * at 0: an all-re-enrollment activation never attempted a vendor activation,
   * and anything that did attempt one and got nowhere is already reported by the
   * failure row below — so "0 devices activated" only ever adds noise.
   */
  successCount: number;
  /** Total devices that failed across the finished bulk batches. */
  failureCount: number;
  /**
   * Devices re-enrolled successfully (already enrolled and still in term).
   * Re-enrollment is settled on the billing side with no vendor activation, so it
   * gets its own row rather than being folded into the activated count.
   */
  reenrollmentSuccessCount?: number;
  /** Devices whose re-enrollment failed on the billing side. */
  reenrollmentFailureCount?: number;
  /**
   * Vehicles enrolled alongside the finished batches in the same OST enroll
   * action (Shop Add-Ons). When > 0, an extra "vehicles enrolled" row is shown
   * so the card is a single combined summary. 0 for every non-OST-enroll batch.
   */
  enrolledVehicleCount?: number;
  productName?: string;
  /**
   * Retries activation for the failed devices. When provided (and there are
   * failures), the failure row shows a clickable "try again" action.
   */
  onRetry?: () => void;
  /** Disables the "try again" action while a retry request is in flight. */
  isRetrying?: boolean;
  /** Dismisses the card (clears the finished batches). */
  onClose: () => void;
  /** Pre-translated terminal-state title; falls back to the peripheral wording built from `productName` when omitted. */
  completeTitle?: string;
  /** Renders the success row text for a given count; falls back to the peripheral "device(s) activated" wording when omitted. */
  activatedText?: (count: number) => string;
  /** Renders the failure row text for a given count; falls back to the peripheral "device(s) unable to activate" wording when omitted. */
  failedText?: (count: number) => string;
  /** Label for the optional bottom action button (e.g. "View details"). */
  primaryActionLabel?: string;
  /** Shows a full-width action button at the bottom of the card when provided. */
  onPrimaryAction?: () => void;
};

/**
 * Terminal state of the activation widget (Figma node 5832:162588): a
 * "…activation complete" card showing the aggregate activated / unable-to-
 * activate counts, dismissable via the close button.
 *
 * Rows are ordered successes first, then failures. Only the vendor-activation
 * failure row offers a retry: a re-enrollment has no activation batch to
 * resubmit, so its failure is reported without one.
 */
export const ActivationCompletionCard = ({
  successCount,
  failureCount,
  reenrollmentSuccessCount = 0,
  reenrollmentFailureCount = 0,
  enrolledVehicleCount = 0,
  productName,
  onRetry,
  isRetrying = false,
  onClose,
  completeTitle,
  activatedText,
  failedText,
  primaryActionLabel,
  onPrimaryAction,
}: ActivationCompletionCardProps): JSX.Element => {
  const { classes } = useStyles();
  const format = useFormat();
  const { getFlag } = useFeatureFlags();
  const localizationEnabled = Boolean(getFlag(LOCALIZATION_FEATURE_FLAG));
  // Subscribe to i18n so imperative `translateText(...)` re-renders on locale
  // change.
  useTranslation(['catalog']);

  const title =
    completeTitle ??
    (productName
      ? translateText(TRANSLATION_KEYS.titleComplete, { productName })
      : translateText(TRANSLATION_KEYS.titleCompleteFallback));

  return (
    <section
      className={classes.root}
      aria-live="polite"
      aria-label={title}
      data-testid="activation-progress-snackbar"
    >
      <div className={classes.header}>
        <Typography
          variant="header2_medium"
          className={classes.title}
          data-testid="activation-progress-title"
        >
          {title}
        </Typography>
        <button
          type="button"
          className={classes.iconButton}
          onClick={onClose}
          aria-label={translateText(TRANSLATION_KEYS.close)}
          data-testid="activation-progress-close"
        >
          <CloseIcon aria-hidden="true" focusable={false} />
        </button>
      </div>

      <div className={classes.body}>
        {enrolledVehicleCount > 0 && (
          <ActivationCompletionRow
            testId="activation-completion-vehicles"
            text={translateText(TRANSLATION_KEYS.enrolledVehicleCount, {
              count: enrolledVehicleCount,
              formattedCount: localizationEnabled
                ? format.number(enrolledVehicleCount, {
                    digits: 0,
                  })
                : String(enrolledVehicleCount),
            })}
          />
        )}
        {successCount > 0 && (
          <ActivationCompletionRow
            testId="activation-completion-success"
            text={
              activatedText?.(successCount) ??
              translateText(TRANSLATION_KEYS.cardActivated, {
                count: successCount,
              })
            }
          />
        )}
        {reenrollmentSuccessCount > 0 && (
          <ActivationCompletionRow
            testId="activation-completion-reenrolled"
            text={translateText(TRANSLATION_KEYS.reenrolled, {
              count: reenrollmentSuccessCount,
            })}
          />
        )}
        {failureCount > 0 && (
          <ActivationCompletionRow
            testId="activation-completion-failure"
            isFailure
            text={
              failedText?.(failureCount) ??
              translateText(TRANSLATION_KEYS.cardFailed, {
                count: failureCount,
              })
            }
          >
            {onRetry && (
              <>
                {' — '}
                <button
                  type="button"
                  className={classes.retryButton}
                  onClick={onRetry}
                  disabled={isRetrying}
                  data-testid="activation-completion-retry"
                >
                  {translateText(TRANSLATION_KEYS.tryAgain)}
                </button>
              </>
            )}
          </ActivationCompletionRow>
        )}
        {reenrollmentFailureCount > 0 && (
          <ActivationCompletionRow
            testId="activation-completion-reenroll-failure"
            isFailure
            text={translateText(TRANSLATION_KEYS.reenrollFailed, {
              count: reenrollmentFailureCount,
            })}
          />
        )}
      </div>
      {onPrimaryAction && primaryActionLabel && (
        <Button
          variant="primary"
          onClick={onPrimaryAction}
          data-testid="activation-completion-primary-action"
        >
          {primaryActionLabel}
        </Button>
      )}
    </section>
  );
};

export default ActivationCompletionCard;
